import {
  desvioDoEsperado,
  idadeEmMeses,
  MINIMO_FILHOS,
  resumoDaProle,
  type Desvio,
  type Esperado,
  type ResumoDaProle,
} from '@equiview/domain';
import type { PontoDeReferencia } from '../components/GraficoEvolucao.js';
import { curvaDaPopulacao, formatarIdade, formatarValor, ROTULO_LEITURA, traitPorCodigo, unidadeCurta, valoresPorFicha } from './catalogo.js';
import type { Animal, Avaliacao, Medida } from './fonte.js';

/**
 * A regua da populacao: todos os animais medidos, com as fichas achatadas. A
 * ficha do animal e o laudo leem daqui, para os dois nunca divergirem.
 */
export interface Populacao {
  animais: readonly Animal[];
  avaliacoes: readonly Avaliacao[];
  valores: ReadonlyMap<string, Record<string, number | null>>;
}

export const montarPopulacao = (animais: readonly Animal[], avaliacoes: readonly Avaliacao[], medidas: readonly Medida[]): Populacao => ({
  animais,
  avaliacoes,
  valores: valoresPorFicha(medidas),
});

/** Medidas da media da prole: um conjunto curto, das que mais dizem sobre o porte. */
export const MEDIDAS_DA_PROLE = ['A_CER', 'C_COR', 'PE_TOR', 'PE_CAN'] as const;

export interface Posicao {
  meses: number;
  valor: number;
  esperado: Esperado | null;
  desvio: Desvio | null;
}

/** Onde a medida desta ficha cai em relacao aos animais da mesma idade (sem contar o proprio). */
export function posicaoNaIdade(pop: Populacao, animal: Animal, ficha: Avaliacao, codigo: string): Posicao | null {
  const valor = pop.valores.get(ficha.id)?.[codigo];
  if (valor == null) return null;
  const meses = idadeEmMeses(animal.birth_date, ficha.visit_date);
  const esperado = curvaDaPopulacao(codigo, pop.animais, pop.avaliacoes, pop.valores, animal.id).esperadoEm(meses);
  return { meses, valor, esperado, desvio: esperado ? desvioDoEsperado(valor, esperado) : null };
}

/** A faixa do esperado ao longo das idades de um grafico, mes a mes. */
export function serieDeReferencia(pop: Populacao, animal: Animal, codigo: string, de: number, ate: number): PontoDeReferencia[] {
  const curva = curvaDaPopulacao(codigo, pop.animais, pop.avaliacoes, pop.valores, animal.id);
  const r: PontoDeReferencia[] = [];
  for (let x = Math.floor(de); x <= Math.ceil(ate); x++) {
    const e = curva.esperadoEm(x);
    if (e) r.push({ x, min: e.media - e.dp, media: e.media, max: e.media + e.dp });
  }
  return r;
}

/** "Aos 20 meses: 142,3 cm — 3,1 cm acima do esperado para a idade (faixa normal: 135,0–141,0 cm)." */
export function frasePosicao(p: Posicao, codigo: string): string {
  const t = traitPorCodigo.get(codigo);
  const u = unidadeCurta(t);
  const valor = `${formatarValor(p.valor, t)}${u}`;
  if (!p.esperado || !p.desvio) return `Aos ${formatarIdade(p.meses)}: ${valor} — sem referência para esta idade (poucos animais medidos).`;
  const faixa = `${formatarValor(p.esperado.media - p.esperado.dp, t)}–${formatarValor(p.esperado.media + p.esperado.dp, t)}${u}`;
  const diferenca =
    p.desvio.leitura === 'dentro'
      ? 'dentro do esperado para a idade'
      : `${formatarValor(Math.abs(p.desvio.diferenca), t)}${u} ${ROTULO_LEITURA[p.desvio.leitura]} para a idade`;
  return `Aos ${formatarIdade(p.meses)}: ${valor} — ${diferenca} (faixa normal: ${faixa}).`;
}

export interface ProleNaMedida {
  codigo: string;
  resumo: ResumoDaProle | null;
  medidos: number;
  /** Desvio de cada filho, pela ultima ficha dele. */
  porFilho: Map<string, Desvio>;
}

/** Media da prole numa medida: cada filho comparado com o esperado para a idade dele. */
export function proleNaMedida(pop: Populacao, filhos: readonly Animal[], codigo: string): ProleNaMedida {
  const porFilho = new Map<string, Desvio>();
  const medidos: Array<{ valor: number; esperado: Esperado }> = [];
  for (const filho of filhos) {
    const ultima = pop.avaliacoes
      .filter((a) => a.animal_id === filho.id && pop.valores.get(a.id)?.[codigo] != null)
      .sort((x, y) => x.visit_date.localeCompare(y.visit_date))
      .at(-1);
    if (!ultima) continue;
    const p = posicaoNaIdade(pop, filho, ultima, codigo);
    if (!p?.esperado || !p.desvio) continue;
    medidos.push({ valor: p.valor, esperado: p.esperado });
    porFilho.set(filho.id, p.desvio);
  }
  return { codigo, resumo: resumoDaProle(medidos), medidos: medidos.length, porFilho };
}

/** "Os filhos ficam, em media, 2,4 cm acima do esperado na altura de cernelha (4 filhos medidos)." */
export function fraseProle(p: ProleNaMedida): string {
  const t = traitPorCodigo.get(p.codigo);
  if (!p.resumo) return `${t?.label ?? p.codigo}: precisa de pelo menos ${MINIMO_FILHOS} filhos medidos (${p.medidos} até agora).`;
  const r = p.resumo;
  const quanto =
    r.leitura === 'dentro'
      ? 'na média do esperado'
      : `em média ${formatarValor(Math.abs(r.mediaDaDiferenca), t)}${unidadeCurta(t)} ${ROTULO_LEITURA[r.leitura]}`;
  return `${t?.label ?? p.codigo}: ${quanto} (${r.n} filhos medidos).`;
}
